import { useCallback, useMemo, useRef, useState } from 'react'
import { useCurrentLanguage } from '@/core/utils'
import { verifyReCaptchaV3 } from './recaptcha-v3.service'

type VerifiedAction = () => unknown | Promise<unknown>

export const useRecaptchaGate = () => {
    const language = useCurrentLanguage()
    const pendingActionRef = useRef<VerifiedAction | null>(null)
    const [isVerifying, setIsVerifying] = useState(false)
    const [fallbackOpen, setFallbackOpen] = useState(false)

    const closeFallback = useCallback(() => {
        pendingActionRef.current = null
        setFallbackOpen(false)
    }, [])

    const handleFallbackVerified = useCallback(() => {
        const action = pendingActionRef.current
        pendingActionRef.current = null
        setFallbackOpen(false)
        if (action) void action()
    }, [])

    const runWithRecaptcha = useCallback(async (actionName: string, action: VerifiedAction, enabled = true) => {
        if (!enabled) {
            await action()
            return
        }

        setIsVerifying(true)
        const passed = await verifyReCaptchaV3(actionName)
        setIsVerifying(false)

        if (passed) {
            await action()
            return
        }

        pendingActionRef.current = action
        setFallbackOpen(true)
    }, [])

    const modalProps = useMemo(
        () => ({
            open: fallbackOpen,
            language: language.toLowerCase(),
            onClose: closeFallback,
            onVerified: handleFallbackVerified
        }),
        [closeFallback, fallbackOpen, handleFallbackVerified, language]
    )

    return {
        isVerifying,
        runWithRecaptcha,
        modalProps
    }
}
